import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import styled from "styled-components";

import SvgLoadingAnimation from "../../components/loading";
import Button from "../../components/button/Button";
import { useCreateDemoSessionMutation } from "../../app/services/demo";
import {
  getAuthTokenFromCookies,
  setAuthTokenToCookies,
  syncSessionBaseline,
} from "../../common/utils";
import { clearActAs } from "../../utils/actAs";
import { clearDemoMode, enterDemoMode, isDemoMode } from "../../utils/demoMode";
import {
  useSignoutUserMutation,
  useGetCurrentUserQuery,
  // @ts-expect-error -- 型宣言なしの JS モジュール
} from "../../app/accounts/authuser";

const PageContainer = styled.div.attrs({ className: "u-col-center u-w100" })`
  min-height: 100dvh;
  justify-content: center;
  padding: 48px 16px;
`;

const Card = styled.div.attrs({ className: "u-col-center u-gap16" })`
  width: 100%;
  max-width: 480px;
  padding: 48px 24px;
  border-radius: 12px;
  border: 1px solid var(--soremo-border);
  background: #fff;
  box-shadow: 2px 4px 8px rgba(0, 0, 0, 0.05);
  text-align: center;

  .material-symbols-rounded {
    font-size: 48px;
    color: var(--soremo-light-gray);
  }
`;

const CardActions = styled.div.attrs({ className: "u-col-center u-gap8 u-w100" })``;

type EntryStatus = "confirm" | "loading" | "expired" | "not-found" | "error";

/**
 * デモリンクのランディングページ（/demo/:token）。
 *
 * 管理者が発行したデモリンクを開くと、トークンをデモ閲覧アカウントの
 * DRF Token に交換し、通常サインインと同じ Cookie に保存してプロジェクト
 * 画面へフルリロード遷移する（RTK Query キャッシュを確実にリセットする
 * ため、SPA 内遷移ではなく location.replace を使う）。
 *
 * 通常のアカウントでサインイン中のブラウザで開かれた場合は、黙って
 * セッションを乗っ取らず確認を挟む。デモ用 Cookie で上書きするだけでは
 * BE の Django セッションが残り（`/api/me` 等は SessionAuthentication も
 * フォールバックに持つ）、「表示は元のユーザーなのにデモの警告が出る」
 * 混乱を生むため、承諾を得てから `POST /api/logout` まで実行する。
 *
 * 以降の閲覧は既存の全画面がそのまま動作し、書き込みは
 * createAuthBaseQuery のデモガードと BE の DemoReadOnlyMiddleware が遮断する。
 */
const DemoEntry: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const [createDemoSession] = useCreateDemoSessionMutation();
  const [signoutUser] = useSignoutUserMutation();
  const [entryStatus, setEntryStatus] = useState<EntryStatus>(() =>
    // 通常サインイン中（デモ中の別リンク遷移は除く）なら確認から始める
    getAuthTokenFromCookies() && !isDemoMode() ? "confirm" : "loading",
  );
  const [isSigningOut, setIsSigningOut] = useState(false);
  const startedRef = useRef(false);

  // 確認画面で「誰としてサインインしているか」を示すために現在ユーザーを引く。
  // サインアウト開始後は購読を切る（キャッシュ破棄に反応して認証切れの
  // /api/me を投げ直さないようにする）。
  const { data: currentUser } = useGetCurrentUserQuery(undefined, {
    skip: entryStatus !== "confirm" || isSigningOut,
  });
  const signedInName: string =
    currentUser?.data?.display_name_jp || currentUser?.data?.display_name_en || "";

  // 外部公開ページのため検索エンジンにインデックスさせない
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    return () => {
      document.head.removeChild(meta);
    };
  }, []);

  useEffect(() => {
    if (!token || entryStatus !== "loading" || startedRef.current) return;
    startedRef.current = true;

    const exchange = async () => {
      try {
        const result = await createDemoSession({ token }).unwrap();
        const { data } = result;

        // 以前のセッション状態（代理操作・旧デモ）を必ず捨ててから入る
        clearActAs();
        clearDemoMode();
        setAuthTokenToCookies(data.token);
        enterDemoMode({
          token,
          projectId: data.project_id,
          position: data.position,
          displayName: data.display_name,
        });
        // 自分で切り替えたので SessionSwitchGuard を発火させない
        // （enterDemoMode の後は Cookie 操作が続かないため明示的に呼ぶ）
        syncSessionBaseline();

        // フルリロード遷移で RTK Query キャッシュをリセットする。
        // 初回表示画面はリンクごとの設定（landing_tab）に従い、home 以外は
        // ProjectWrapper が解釈する ?tab= クエリで指定する。
        const landingPath =
          data.landing_tab && data.landing_tab !== "home"
            ? `/${data.project_id}?tab=${data.landing_tab}`
            : `/${data.project_id}`;
        window.location.replace(landingPath);
      } catch (error) {
        const status = (error as { status?: number })?.status;
        if (status === 410) {
          setEntryStatus("expired");
        } else if (status === 404) {
          setEntryStatus("not-found");
        } else {
          setEntryStatus("error");
        }
      }
    };

    void exchange();
  }, [token, entryStatus, createDemoSession]);

  const handleSignoutAndStart = useCallback(async () => {
    setIsSigningOut(true);
    try {
      // redirectTo: null で /signin へ飛ばさず、この画面でデモ入場を続ける。
      // 通信に失敗しても FE 側の掃除は mutation 内で完了する。
      await signoutUser({ redirectTo: null }).unwrap();
    } catch {
      // BE のセッション破棄に失敗しても、デモ用 Token で上書きして続行する
    }
    setEntryStatus("loading");
  }, [signoutUser]);

  const handleCancel = useCallback(() => {
    // サインインしたままホームへ戻す。replace でデモ URL を履歴に残さず、
    // 戻るボタンで意図せず再入場することを防ぐ。
    window.location.replace("/");
  }, []);

  if (entryStatus === "confirm") {
    return (
      <PageContainer>
        <Card>
          <span className="material-symbols-rounded">switch_account</span>
          <h1 className="heading-spacing-18">サインアウトしてデモを開きます</h1>
          <p className="bodytext-13 u-text-gray" style={{ whiteSpace: "pre-wrap" }}>
            {`このブラウザは現在${signedInName ? `「${signedInName}」` : "別のアカウント"}でサインインしています。\nデモは専用の閲覧アカウントで表示するため、続けるとサインアウトします（開いている他のタブも同時にサインアウトされます）。\nサインインしたまま確認したい場合は、シークレットウィンドウでこの URL を開いてください。`}
          </p>
          <CardActions>
            <Button
              size="small"
              theme="primary"
              disabled={isSigningOut}
              onClick={handleSignoutAndStart}
            >
              {isSigningOut ? "サインアウト中..." : "サインアウトしてデモを見る"}
            </Button>
            <Button
              size="small"
              theme="tertiary"
              disabled={isSigningOut}
              onClick={handleCancel}
            >
              キャンセル（サインインを続ける）
            </Button>
          </CardActions>
        </Card>
      </PageContainer>
    );
  }

  if (entryStatus === "loading") {
    return (
      <PageContainer>
        <Card>
          <SvgLoadingAnimation />
          <p className="bodytext-13 u-text-light-gray">デモを準備しています...</p>
        </Card>
      </PageContainer>
    );
  }

  const content =
    entryStatus === "expired"
      ? {
          icon: "hourglass_disabled",
          title: "このデモリンクは終了しました",
          message:
            "リンクの有効期限が切れているか、無効化されています。\nお手数ですが、リンクの発行元にお問い合わせください。",
        }
      : entryStatus === "not-found"
        ? {
            icon: "link_off",
            title: "デモリンクが見つかりません",
            message:
              "URL が正しいかご確認ください。\nお手数ですが、リンクの発行元にお問い合わせください。",
          }
        : {
            icon: "error",
            title: "デモを開始できませんでした",
            message:
              "一時的な問題が発生した可能性があります。\n少し時間をおいて、もう一度お試しください。",
          };

  return (
    <PageContainer>
      <Card>
        <span className="material-symbols-rounded">{content.icon}</span>
        <h1 className="heading-spacing-18">{content.title}</h1>
        <p className="bodytext-13 u-text-gray" style={{ whiteSpace: "pre-wrap" }}>
          {content.message}
        </p>
        {entryStatus === "error" && (
          <Button size="small" theme="primary" onClick={() => window.location.reload()}>
            再試行
          </Button>
        )}
      </Card>
    </PageContainer>
  );
};

export default DemoEntry;
